import React, { useState, useEffect, useRef } from "react";
import "../styles/pages/Making.scss";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAudioBookList,
  getAudioBookDetail,
  deleteAudioBooks,
} from "../api/LibraryAPI";
import { decryptUrlSafe, encryptUrlSafe } from "../utils/encryption";

// 분리된 컴포넌트 임포트
import AudioSharingPC from "../components/sharing/AudioSharingPC";
import AudioSharingMobile from "../components/sharing/AudioSharingMobile";

const AudioSharing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const params = new URLSearchParams(window.location.search);

  const USER_ID = localStorage.getItem("userID");
  const BOOK_ID = decryptUrlSafe(params.get("book_id"));
  const pageName = location.state?.pageName || "";
  const bookID = location.state?.bookID || BOOK_ID;

  const [bookData, setBookData] = useState(null);
  const [story, setStory] = useState([]);
  const [index, setIndex] = useState(-1);
  const [audioSrc, setAudioSrc] = useState("");
  const [bgImage, setBgImage] = useState("");
  const [contentText, setContentText] = useState("");
  const [currentAudioIndex, setCurrentAudioIndex] = useState(null);
  const [showFinalReadingModal, setShowFinalReadingModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [audioBookAll, setAudioBookAll] = useState([]);
  const [audioBookList, setAudioBookList] = useState([]);
  const [showAudioBookModal, setShowAudioBookModal] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isBookOpened, setIsBookOpened] = useState(false);
  const [showFlipbook, setShowFlipbook] = useState(false);
  const [showTitleOnCover, setShowTitleOnCover] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);

  // 모바일 감지 상태
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const handleAudioToggle = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        if (audioRef.current.ended) {
          audioRef.current.currentTime = 0;
        }
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch((err) => {
            console.error("오디오 재생 실패:", err);
            setIsPlaying(false);
          });
      }
    }
  };


  // 화면 크기 변화 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchAudioBook = async () => {
      const res = await getAudioBookList(bookID);

      if (!res.success || res.data.audiobook_count === 0) {
        setErrorMessage("오디오북이 없습니다.");
        setShowErrorModal(true);
        return;
      }

      setAudioBookAll(res.data);
      setAudioBookList(res.data.audiobooks);
      setShowAudioBookModal(true);
    };
    fetchAudioBook();
  }, []);

  const handleSelectAudioBook = async (audiobook_id) => {
    const res = await getAudioBookDetail(audiobook_id);
    if (!res.success) {
      setErrorMessage("오디오북을 불러올 수 없습니다");
      setShowErrorModal(true);
      return;
    }
    const data = res.data;
    setBookData({
      title: data.book_title,
      bookcover: data.bookcover,
      style: data.style,
      author: data.author,
    });
    console.log("res data", data);
    setStory(data.pages || []);
    setIndex(-1);
    setShowAudioBookModal(false);
  };

  useEffect(() => {
    if (index === -1) {
      if (bookData) {
        setAudioSrc(""); // 표지일 때는 오디오 없음
        setBgImage(bookData.bookcover);
        setContentText(bookData.title);
        // 상태 초기화
        setIsBookOpened(false);
        setShowFlipbook(false);
      }
      return;
    }
    if (!story || !story[index]) return;
    const current = story[index];
    setBgImage(current.background);
    setContentText(current.page_content);
    setAudioSrc(current.audio_url);
  }, [index, story]);

  useEffect(() => {
    if (!audioSrc || currentAudioIndex === index) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = audioSrc;

      // 모바일 세로 모드에서 첫 페이지 진입 시 자동 재생 차단 (회전 모달 대응)
      const isPortrait = window.innerHeight > window.innerWidth;
      if (isMobile && isPortrait && index === 0 && (currentAudioIndex === null || currentAudioIndex === -1)) {
        setCurrentAudioIndex(index);
        return;
      }

      audioRef.current
        .play()
        .then(() => {
          setCurrentAudioIndex(index);
        })
        .catch((err) => console.error("오디오 재생 실패:", err));
    }
  }, [audioSrc, index, isMobile]);

  const handleButtonClick = (direction) => {
    setIndex((prev) => {
      let newIndex = prev + (direction === "left" ? -1 : 1);
      if (newIndex < -1) newIndex = -1;
      else if (newIndex >= story.length) {
        setShowFinalReadingModal(true);
        return prev;
      }
      return newIndex;
    });
  };

  // 오디오북 삭제
  const handleDeleteAudioBooks = async (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) return;

    const res = await deleteAudioBooks(selectedIds);

    if (res.success) {
      toast.success(`${res.data.deleted_count}개의 오디오북을 삭제했어요.`);

      const updated = await getAudioBookList(bookID);

      if (!updated.success || updated.data.audiobooks.length === 0) {
        navigate("/myStorybook");
        return;
      }

      setAudioBookAll(updated.data);
      setAudioBookList(updated.data.audiobooks);
    } else {
      toast.error(res.errorMessage || "삭제 중 오류가 발생했어요.");
      setShowErrorModal(true);
    }
  };

  const handleExit = () => {
    setShowFinalReadingModal(false);
  };

  const commonProps = {
    bookData,
    story,
    index,
    setIndex,
    showFinalReadingModal,
    setShowFinalReadingModal,
    showErrorModal,
    setShowErrorModal,
    errorMessage,
    bgImage,
    contentText,
    handleButtonClick,
    handleExit,
    audioRef,
    pageName,
    navigate,
    showAudioBookModal,
    setShowAudioBookModal,
    audioBookList,
    handleSelectAudioBook,
    handleDeleteAudioBooks,
    bookID,
    isFlipping,
    setIsFlipping,
    isBookOpened,
    setIsBookOpened,
    showFlipbook,
    setShowFlipbook,
    showTitleOnCover,
    setShowTitleOnCover,
    encryptUrlSafe,
    isPlaying,
    setIsPlaying,
    handleAudioToggle,
  };

  return isMobile ? (
    <AudioSharingMobile {...commonProps} />
  ) : (
    <AudioSharingPC {...commonProps} />
  );
};


export default AudioSharing;
